import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Button, IconButton} from '@mui/material';
import classNames from 'classnames';
import moment from 'moment';
import {FormattedMessage} from 'react-intl';
import {useMediaQuery} from 'react-responsive';
import {SVGS} from '../../assets/images';
import ScheduleDateSelector from '../../components/Schedule/header/components/schedule-date-selector';
import PageHeader from '../../components/shared/page-header';
import PageWrapperV2 from '../../components/shared/page-wrapper-v2';
import SearchInput from '../../components/shared/search-input';
import {RangeType} from '../../components/shared/calendar/types';
import {getAllEmployees} from '../../features/employees/short';
import {setIndicatorLoading} from '../../features/indicator';
import {updateTimeTrackingDateRangeFilter, updateTimeTrackingPropertyFilter} from '../../features/time-tracking-filters';
import {updateVacationReportPropertyFilter} from '../../features/vacation';
import {getWorkCalendar, getWorkCalendars} from '../../features/settings/workcalendar';
import {Year} from '../../features/settings/workcalendar/types';
import {useAppDispatch, useAppSelector} from '../../hooks/hooks';
import { canSeeOtherVacations, canSeeVacationReportPage } from '../../features/profile/entityPermissions';
import { REmployees } from '../../features/roles/types';
import useVacationReportLoader from './loader';
import VacationDiagramAdapter from './components/redesign-components/redesign-adapter';
import VacationMobileFilterSheet from './components/top-bar/component/mobile-filter-sheet';
import VacationReportFilter from './components/top-bar/component/filter';
import Legend from './components/top-bar/component/legend';
import {FilterState} from './components/top-bar/component/filter/types';
import VacationReportSearchProvider, {useVacationReportSearch} from './search';
import styles from './vacation-report.module.scss';
import topBarStyles from './components/top-bar/vacation-report-top-bar.module.scss';
import TimeTrackingPageBody from '../../components/shared/time-tracking-page-body';
import MobileInfoPopup from '../../components/shared/mobile-info-popup';
import { getDefaultMonthRange, getSelectedDateRangeFromFilter, mapWorkCalendarYears } from '../../components/shared/calendar/utils';

export default function VacationReportPage() {
    return (
        <VacationReportSearchProvider>
            <VacationReportPageContent />
        </VacationReportSearchProvider>
    );
}

function VacationReportPageContent() {
    const today = moment();
    const currentYear = today.year();
    const [years, setYears] = useState<Array<Year>>([]);
    const dispatch = useAppDispatch();

    // selector
    const employees = useAppSelector((selector) => selector.rEmployees);
    const profile = useAppSelector((selector) => selector.profile);
    const { data, loading: dataLoading, dateFilter } = useVacationReportLoader();
    const { change: searchChange, value: search } = useVacationReportSearch();
    const dateRangeFilter = useAppSelector((selector) => selector.timeTrackingFilters.filter.dateRangeFilter);
    const generalFilter = useAppSelector((selector) => selector.timeTrackingFilters.filter);
    const statuses = useAppSelector((selector) => selector.vacationsReport.filter.propertyFilter.selectedVacationStates);

    const [isFilterModalOpen, setFilterModalOpen] = useState(false);
    const [isDateSelectorOpen, setDateSelectorOpen] = useState(false);

    const canViewVacationReport = canSeeVacationReportPage(profile);
    const canViewOtherVacations = canSeeOtherVacations(profile);
    const ownEmployee = useMemo<REmployees | null>(() => {
        if (!profile.id) {
            return null;
        }

        return employees.find((employee) => employee.id === profile.id)
            ?? { id: profile.id, name: profile.name, isDefault: false };
    }, [employees, profile.id, profile.name]);
    const filterEmployees = useMemo<REmployees[]>(() => (
        canViewOtherVacations
            ? employees
            : ownEmployee
                ? [ownEmployee]
                : []
    ), [canViewOtherVacations, employees, ownEmployee]);
    const controlsDisabled = false;
    const isMobile = useMediaQuery({ maxWidth: 1000 });

    const allowedRangeTypes = [
        RangeType.CurrentMonth,
        RangeType.PreviousMonth,
        RangeType.CurrentQuarter,
        RangeType.PreviousQuarter,
        RangeType.CurrentYear,
        RangeType.PreviousYear,
    ];

    const handleDateRangeChange = (values: moment.Moment[]) => new Promise<void>((resolve) => {
        updateTimeTrackingDateRangeFilter({
            startDate: values[0].format('YYYY-MM-DD'),
            endDate: values[1].format('YYYY-MM-DD'),
        });
        resolve();
    });

    const defaults = useMemo(() => getDefaultMonthRange(today), [today.valueOf()]);

    const calendarYears = useMemo(() => mapWorkCalendarYears(years), [years]);

    const selectedDates = useMemo(
        () => getSelectedDateRangeFromFilter(dateRangeFilter, defaults),
        [dateRangeFilter, defaults]
    );

    const hasDate = useMemo(() => {
        const [s, e] = selectedDates;
        return !(s.isSame(defaults.startDate, 'day') && e.isSame(defaults.endDate, 'day'));
    }, [selectedDates, defaults]);

    const rawSelectedEmployees = generalFilter.propertyFilter.selectedEmployees ?? [];
    const selectedEmployees = canViewOtherVacations
        ? rawSelectedEmployees
        : [];
    const selectedVacationStates = statuses ?? [];

    const hasSelectedEntities =
        selectedEmployees.length > 0 ||
        selectedVacationStates.length > 0;

    const hasSearch = (search?.trim()?.length ?? 0) > 0;
    const hasAnyFilters = hasSearch || hasDate || hasSelectedEntities;

    const handlePropertyFilterChanged = (value: FilterState) => new Promise<void>((resolve) => {
        updateVacationReportPropertyFilter({
            selectedEmployees: canViewOtherVacations ? value.selectedEmployees ?? [] : [],
            selectedVacationStates: value.selectedVacationStates ?? [] });
        updateTimeTrackingPropertyFilter({
            selectedEmployees: canViewOtherVacations ? value.selectedEmployees ?? [] : [],
            selectedProjects: generalFilter.propertyFilter.selectedProjects ?? [],
            selectedShiftTypes: generalFilter.propertyFilter.selectedShiftTypes ?? [],
            selectedAbsenceTypes: generalFilter.propertyFilter.selectedAbsenceTypes ?? [],
            showAbsences: generalFilter.propertyFilter.showAbsences,
        });
        resolve();
    });

    const handleResetFilters = () => {
        updateTimeTrackingPropertyFilter({
            selectedProjects: [], selectedEmployees: [],
            selectedShiftTypes: [], selectedAbsenceTypes: [], selectedStatuses: [], showAbsences: true
        });
        updateVacationReportPropertyFilter({selectedEmployees:[],selectedVacationStates:[]})
        searchChange('');
        handleDateRangeChange([defaults.startDate, defaults.endDate]);
    };

    const propertyFilters = useMemo<FilterState>(() => ({
        selectedEmployees,
        selectedVacationStates,
    }), [selectedEmployees, selectedVacationStates]);

    const mobileFiltersOpen = isMobile && isFilterModalOpen;

    const handleDateSelectorOpenChange = useCallback((open: boolean) => {
        setDateSelectorOpen(open);
        if (open) {
            setFilterModalOpen(false);
        }
    }, []);

    const dateSelectorState = useMemo(() => ({
        open: isDateSelectorOpen,
        setOpen: handleDateSelectorOpenChange
    }), [isDateSelectorOpen, handleDateSelectorOpenChange]);

    const shouldLockScroll = isMobile && (mobileFiltersOpen || isDateSelectorOpen);

    useEffect(() => {
        if (canViewVacationReport && canViewOtherVacations && employees.length < 1) {
            dispatch(getAllEmployees());
        }
    }, [canViewOtherVacations, canViewVacationReport, dispatch, employees.length]);

    useEffect(() => {
        if (!canViewVacationReport) {
            return;
        }

        dispatch(getWorkCalendars()).unwrap().then((value) => {
            const primaryCalendar = value[0]!;
            if (!primaryCalendar) {
                return;
            }

            [currentYear - 1, currentYear].forEach((x) => {
                dispatch(getWorkCalendar({ workCalendarId: primaryCalendar.id, year: x })).unwrap().then((ry) => {
                    setYears((y) => {
                        if (!y.find((yy) => yy.year === ry.year)) {
                            const state = [...y];
                            state.push(ry)
                            return state;
                        }
            
                        return y;
                    })
                })
            });
        });
    }, [canViewVacationReport, currentYear, dispatch]);

    useEffect(() => {
        if (canViewOtherVacations || rawSelectedEmployees.length < 1) {
            return;
        }

        updateVacationReportPropertyFilter({
            selectedEmployees: [],
            selectedVacationStates,
        });
        updateTimeTrackingPropertyFilter({
            ...generalFilter.propertyFilter,
            selectedEmployees: [],
        });
    }, [canViewOtherVacations, generalFilter.propertyFilter, rawSelectedEmployees.length, selectedVacationStates]);

    useEffect(() => {
        setIndicatorLoading(dataLoading);
        return () => {
            setIndicatorLoading(false);
        };
    }, [dataLoading]);

    useEffect(() => {
        if (!dateRangeFilter?.startDate || !dateRangeFilter?.endDate) {
            updateTimeTrackingDateRangeFilter({
                startDate: defaults.startDate.format('YYYY-MM-DD'),
                endDate:   defaults.endDate.format('YYYY-MM-DD'),
            });
        }
    }, []);

    useEffect(() => {
        if (!isMobile) {
            setFilterModalOpen(false);
            setDateSelectorOpen(false);
        }
    }, [isMobile]);

    useEffect(() => {
        if (!shouldLockScroll || typeof document === 'undefined') {
            return;
        }

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [shouldLockScroll]);

    const diagram = !mobileFiltersOpen ? (
        <div className={styles.diagramScroll}>
            <VacationDiagramAdapter
                vacationReportItems={data}
                years={years}
                dateFilter={dateFilter} />
        </div>
    ) : null;

    const header = isMobile ? (
        <div className={classNames(topBarStyles.container, topBarStyles.mobileContainer)}>
            <div className={topBarStyles.mobileHeaderRow}>
                <div className={topBarStyles.titleWithSpinner}>
                    <div className={topBarStyles.title}>
                        <FormattedMessage id={'vacation_report'} />
                    </div>
                </div>
                <div className={topBarStyles.mobileHeaderActions}>
                    <MobileInfoPopup>
                        <Legend className={topBarStyles.mobileLegendContent} />
                    </MobileInfoPopup>
                    <IconButton
                        className={classNames(
                            topBarStyles.mobileIconButton,
                            {
                                [topBarStyles.mobileIconButtonActive]: hasAnyFilters || isFilterModalOpen,
                                [topBarStyles.mobileIconButtonOpen]: isFilterModalOpen,
                            }
                        )}
                        size={'small'}
                        onClick={() => {
                            if (controlsDisabled) {
                                return;
                            }
                            setFilterModalOpen((value) => {
                                const next = !value;
                                if (next) {
                                    setDateSelectorOpen(false);
                                }
                                return next;
                            });
                        }}
                        disabled={controlsDisabled}
                        disableRipple
                        disableFocusRipple
                        disableTouchRipple
                    >
                        <SVGS.FilterAltRounded />
                    </IconButton>
                </div>
            </div>
        </div>
    ) : (
        <div className={topBarStyles.container}>
            <Box className={topBarStyles.header}>
                <div className={classNames({
                    [topBarStyles.headerItem]: true,
                    [topBarStyles.dateSelector]: true
                })}>
                    <div className={topBarStyles.titleWithSpinner}>
                        <div className={topBarStyles.title}>
                            <FormattedMessage id={'vacation_report'} />
                        </div>
                    </div>
                </div>
                <Box className={classNames(topBarStyles.dateSelector, topBarStyles.headerCalendar)}>
                    <ScheduleDateSelector
                        selectedDates={selectedDates}
                        onRangeSelected={handleDateRangeChange}
                        years={calendarYears}
                        minDate={today.clone().add(-365, 'days')}
                        allowedRangeTypes={allowedRangeTypes}
                        calendarState={isMobile ? dateSelectorState : undefined}
                        disabled={controlsDisabled}
                    />
                </Box>
            </Box>
        </div>
    );

    const body = isMobile ? (
        <>
            {!mobileFiltersOpen && (
                <div className={topBarStyles.mobileDateRow}>
                    <div className={topBarStyles.mobileDateSelector}>
                        <ScheduleDateSelector
                            selectedDates={selectedDates}
                            onRangeSelected={handleDateRangeChange}
                            years={calendarYears}
                            minDate={today.clone().add(-365, 'days')}
                            allowedRangeTypes={allowedRangeTypes}
                            calendarState={isMobile ? dateSelectorState : undefined}
                            disabled={controlsDisabled}
                        />
                    </div>
                </div>
            )}

            <VacationMobileFilterSheet
                open={mobileFiltersOpen && !controlsDisabled}
                onClose={() => setFilterModalOpen(false)}
                onApply={handlePropertyFilterChanged}
                onClear={handleResetFilters}
                search={search ?? ''}
                onSearchChange={searchChange}
                filters={propertyFilters}
                employees={filterEmployees}
                disabled={controlsDisabled}
                disableEmployeesFilter={!canViewOtherVacations}
            />

            {diagram}
        </>
    ) : (
        <>
            <div className={topBarStyles.container}>
                <Box className={topBarStyles.subheader}>
                    <div className={topBarStyles.searchFilter}>
                        <SearchInput
                            searchValue={search ?? ''}
                            onChange={searchChange}
                            width='260px'
                            disabled={controlsDisabled}
                        />
                        <VacationReportFilter
                            hasSelected={hasSelectedEntities}
                            onChange={handlePropertyFilterChanged}
                            disabled={controlsDisabled}
                            compactNarrowDesktop
                            employees={filterEmployees}
                            disableEmployeesFilter={!canViewOtherVacations} />
                        {hasAnyFilters && (
                            <Button className={topBarStyles.resetButton} onClick={handleResetFilters} disabled={controlsDisabled}>
                                <SVGS.CancelIcon className={topBarStyles.fill}/>
                            </Button>
                        )}
                    </div>
                    <div className={topBarStyles.info}>
                        <Legend />
                    </div>
                </Box>
            </div>
            {diagram}
        </>
    );

    if (!canViewVacationReport) {
        return null;
    }

    return <PageWrapperV2 className={styles.pageScope}>
        <PageHeader>
            {header}
        </PageHeader>
        <TimeTrackingPageBody justifyContent={'flex-start'} alignItems={'stretch'}>
            <div className={styles.pageBodyWrapper}>
                <div className={classNames(styles.pageBodyContent, { [styles.scrollLocked]: shouldLockScroll })}>
                    {body}
                </div>
            </div>
        </TimeTrackingPageBody>
    </PageWrapperV2>
}
